import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageSquare, Search, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("clinic_admin_token")}` };
}

export default function WhatsApp() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sendForm, setSendForm] = useState({ phone: "", message: "", channel: "twilio" });
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/whatsapp/messages`, { headers: authHeader() });
      setMessages(data);
    } catch { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!sendForm.phone || !sendForm.message) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/whatsapp/send`, sendForm, { headers: authHeader() });
      if (data.success) {
        toast.success("Message sent");
      } else {
        toast.warning("Message queued — check WhatsApp credentials in Settings");
      }
      setSendForm(p => ({ ...p, phone: "", message: "" }));
      fetchMessages();
    } catch { toast.error("Failed to send message"); }
    finally { setSending(false); }
  };

  const filtered = messages.filter(m =>
    !search ||
    m.phone?.includes(search) ||
    m.message?.toLowerCase().includes(search.toLowerCase())
  );

  // Group by phone
  const conversations = filtered.reduce((acc, m) => {
    if (!acc[m.phone]) acc[m.phone] = [];
    acc[m.phone].push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="whatsapp-page">
      <div>
        <h1 className="text-2xl font-bold font-outfit text-slate-900">WhatsApp</h1>
        <p className="text-slate-500 text-sm">{messages.length} total messages across all channels</p>
      </div>

      {/* Send Message Form */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-semibold font-outfit text-slate-900 mb-4 flex items-center gap-2">
          <Send className="w-4 h-4 text-green-600" />
          Send Message
        </h2>
        <form onSubmit={handleSend} className="flex gap-3 flex-wrap">
          <input
            data-testid="whatsapp-phone-input"
            type="text"
            placeholder="Phone number (e.g. +1234567890)"
            value={sendForm.phone}
            onChange={e => setSendForm(p => ({ ...p, phone: e.target.value }))}
            required
            className="flex-1 min-w-48 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <select
            data-testid="whatsapp-channel-select"
            value={sendForm.channel}
            onChange={e => setSendForm(p => ({ ...p, channel: e.target.value }))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="twilio">Twilio</option>
            <option value="meta">Meta</option>
          </select>
          <input
            data-testid="whatsapp-message-input"
            type="text"
            placeholder="Type your message..."
            value={sendForm.message}
            onChange={e => setSendForm(p => ({ ...p, message: e.target.value }))}
            required
            className="flex-1 min-w-64 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <button
            data-testid="send-whatsapp-btn"
            type="submit"
            disabled={sending}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sending..." : "Send"}
          </button>
        </form>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          data-testid="whatsapp-search"
          type="text"
          placeholder="Search messages..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Message List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading...</div>
        ) : Object.keys(conversations).length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <MessageSquare className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <p>No messages yet</p>
            <p className="text-xs mt-1">Messages will appear here after configuring Twilio or Meta webhooks</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {Object.entries(conversations).map(([phone, msgs]) => (
              <div key={phone} className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-semibold text-xs">
                    {phone.slice(-2)}
                  </div>
                  <div>
                    <div className="font-medium text-slate-800 text-sm">{phone}</div>
                    <div className="text-xs text-slate-400">{msgs.length} messages</div>
                  </div>
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${msgs[0]?.channel === "twilio" ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"}`}>
                    {msgs[0]?.channel}
                  </span>
                </div>
                <div className="space-y-2 ml-10">
                  {msgs.map(m => (
                    <div
                      key={m.id}
                      data-testid={`message-${m.id}`}
                      className={`flex gap-2 items-start text-sm ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`max-w-xs px-3 py-2 rounded-xl text-sm ${
                        m.direction === "outbound"
                          ? "bg-green-600 text-white rounded-br-none"
                          : "bg-slate-100 text-slate-700 rounded-bl-none"
                      }`}>
                        <div className="flex items-center gap-1 mb-0.5">
                          {m.direction === "inbound" ? (
                            <ArrowDownLeft className="w-3 h-3 text-slate-400" />
                          ) : (
                            <ArrowUpRight className="w-3 h-3 text-green-200" />
                          )}
                          <span className={`text-xs ${m.direction === "outbound" ? "text-green-200" : "text-slate-400"}`}>
                            {m.direction}
                          </span>
                        </div>
                        {m.message}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
